import { graphql } from '@apollo/client/react/hoc'

import {
  DateTime,
  localISODate,
  getWeekAhead,
  getToday,
} from '../../helpers/date-helpers'
import { slugify } from '../../helpers'
import { formatHumanDate, formatTime } from '../../helpers/date-format-helpers'
import { ApplicationError, NotFoundError } from '../../helpers/errors'
import getCountry from '../../helpers/get-country'
import analyticsUtils from '../../helpers/analytics-utils'
import getMoviesWithSessions from '../../helpers/get-movies-with-sessions'

import { MOVIE_DAYS_IN_ADVANCE } from '../../constants'
import query from './query.graphql'

export { query }

export const mapPropsToOptions = ({ centre, id }) => ({
  variables: {
    centreSlug: centre.slug,
    movieId: parseInt(id),
    centreId: centre.sys.id,
    startsAt: localISODate(centre.timeZone),
  },
  errorPolicy: 'ignore',
})

export const mapDataToProps = ({
  ownProps: { centre, slug, id },
  data: { movies, movie, cinema: cinemaResp, loading, error },
  staticContext,
}) => {
  if (error) {
    return {
      error: new ApplicationError(error),
    }
  } else if (loading) {
    return { loading }
  } else if (!movie) {
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.MOVIES,
      eventCategory: analyticsUtils.categories.MOVIE_NOT_FOUND,
      eventAction: analyticsUtils.actions.MOVIE_NOT_FOUND_ROUTE,
    })
    return { redirect: `/${centre.slug}/movies` }
  } else if (slug !== slugify(movie.title)) {
    return { redirect: `/${centre.slug}/movie/${id}/${slugify(movie.title)}` }
  } else if (!cinemaResp.cinema) {
    return {
      error: new NotFoundError(),
    }
  }
  const { cinema } = cinemaResp

  const sessions = movie.sessions
    .reduce((acc, session) => {
      const dt = DateTime.fromISO(session.startTime, {
        timeZone: centre.timeZone,
      })
      // Need clone because moment is mutable
      const dayDiff = DateTime.moment
        .duration(dt.clone().startOf('day').diff(getToday(centre.timeZone)))
        .days()

      if (!acc[dayDiff]) {
        acc[dayDiff] = {
          shortName: formatHumanDate(dt),
          name: formatHumanDate(dt, { includeMonth: true }),
          date: dt.clone().format('YYYY-MM-DD'),
          sessions: [],
        }
      }

      acc[dayDiff].sessions.push({
        time: formatTime(dt),
        link: session.bookingUrl,
      })
      return acc
    }, [])
    .filter(sessionsOfDay => sessionsOfDay !== null)

  const flicksDomain =
    getCountry(staticContext) === 'Australia' ? 'com.au' : 'co.nz'
  const flicksLink = `https://www.flicks.${flicksDomain}/movie/${slugify(
    movie.title
  )}`

  const movieWithSessionTimes = {
    duration: movie.runTime,
    title: movie.title,
    image: movie.imageUrl,
    backgroundImage: movie.imageUrl,
    genre: movie.genres[0],
    slug: slugify(movie.title),
    typename: movie.__typename,
    synopsis: movie.synopsis,
    classification: movie.classification.rating,
    trailerUrl: movie.trailerUrl,
    flicksLink,
    sessions,
  }
  return {
    movies: getMoviesWithSessions(movies, centre.timeZone),
    movie: movieWithSessionTimes,
    nextWeek: getWeekAhead(centre.timeZone, MOVIE_DAYS_IN_ADVANCE),
    centre,
    cinema: {
      ...cinema,
      openHours: {
        storeTradingSchedule: cinema.storeTradingSchedule,
        centreTradingSchedule: centre.centreTradingSchedule,
        timeZone: centre.timeZone,
      },
    },
  }
}

const withMovies = graphql(query, {
  options: mapPropsToOptions,
  props: mapDataToProps,
})

export default withMovies
